import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { PageHeader } from "@/components/site/PageHeader";
import { useMemberAuth } from "@/context/MemberAuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full bg-brand-elevated border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-brand-textMuted focus:outline-none focus:ring-2 focus:ring-brand-cyan";

export default function MemberAuth() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { member, login, register } = useMemberAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (member) navigate("/forum", { replace: true });
  }, [member, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "register") {
        await register(name, email, password);
        toast.success("Welcome to the community!");
      } else {
        await login(email, password);
        toast.success("Welcome back!");
      }
      navigate("/forum", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader label="Community" title={mode === "register" ? "JOIN THE COMMUNITY" : "WELCOME BACK"}>
        {mode === "register"
          ? "Create a free account to start topics, reply and help shape the project."
          : "Log in to your community account to post and reply."}
      </PageHeader>

      <section className="pb-28" data-testid="member-auth-page">
        <div className="max-w-md mx-auto px-5 lg:px-8">
          {/* toggle */}
          <div className="grid grid-cols-2 gap-2 p-1 bg-brand-elevated rounded-full mb-8">
            {["login", "register"].map((m) => (
              <button
                key={m}
                onClick={() => { setMode(m); setError(""); }}
                data-testid={`auth-tab-${m}`}
                className={`py-2.5 rounded-full text-sm font-medium transition-colors ${
                  mode === m ? "bg-brand-cyan text-brand-base" : "text-brand-textSec hover:text-white"
                }`}
              >
                {m === "login" ? "Log In" : "Sign Up"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="card-surface p-7" data-testid="member-auth-form">
            {error && (
              <div className="mb-5 bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-lg px-4 py-3" data-testid="member-auth-error">
                {error}
              </div>
            )}
            {mode === "register" && (
              <>
                <label className="text-sm text-brand-textSec mb-2 block">Display name</label>
                <input required value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="member-name-input" placeholder="How you'll appear on the forum" />
              </>
            )}
            <label className="text-sm text-brand-textSec mb-2 block mt-4">Email</label>
            <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="member-email-input" placeholder="you@email.com" />
            <label className="text-sm text-brand-textSec mb-2 block mt-4">Password</label>
            <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="member-password-input" placeholder={mode === "register" ? "At least 6 characters" : "••••••••"} />
            <button type="submit" disabled={loading} className="btn-primary mt-6 w-full py-3 text-sm disabled:opacity-60" data-testid="member-auth-submit">
              {loading ? "Please wait…" : mode === "register" ? "Create Account" : "Log In"}
            </button>
          </form>

          <p className="text-brand-textMuted text-xs text-center mt-6">
            By joining you agree to our <Link to="/legal/terms" className="text-brand-cyan">Terms</Link> and{" "}
            <Link to="/legal/privacy" className="text-brand-cyan">Privacy Policy</Link>.
          </p>
        </div>
      </section>
    </>
  );
}
